import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { sortRows, SortableTh, toggleSort } from '../utils/sortTable';

const emptyProductForm = () => ({ code: '', category: '', name: '', description: '', brand: '', unit: 'Adet', default_purchase_price: 0, default_sale_price: 0, default_labor_price: 0, currency: 'USD', labor_hours: '', is_active: true });
const SOURCE_LABELS = { excel: 'Excel', manual: 'Elle', quote: 'Teklif', initial: 'İlk kayıt' };
const fmt = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyStockMovement = () => ({ location_id: '', movement_type: 'in', quantity: '', reason: '', note: '', movement_date: new Date().toISOString().slice(0, 10) });

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPurchasePrices, setShowPurchasePrices] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState(null);
  const [purchaseHistoryProduct, setPurchaseHistoryProduct] = useState(null);
  const [locations, setLocations] = useState([]);
  const [stockProduct, setStockProduct] = useState(null);
  const [stockForm, setStockForm] = useState(emptyStockMovement());
  const salesFileRef = useRef();
  const purchaseFileRef = useRef();

  const [form, setForm] = useState(emptyProductForm());

  // Müşteri / marka filtreleri ve fiyat geçmişi
  const [customers, setCustomers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [customerPrices, setCustomerPrices] = useState([]);
  const [onlyCustomerPriced, setOnlyCustomerPriced] = useState(true);
  const [filterBrand, setFilterBrand] = useState('');
  const [priceHistory, setPriceHistory] = useState(null);
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);

  useEffect(() => {
    api.get('/api/customers/').then(res => setCustomers(res.data)).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!filterCustomer) { setCustomerPrices([]); return; }
    api.get(`/api/customer-prices/customer/${filterCustomer}`)
      .then(res => setCustomerPrices(res.data))
      .catch(() => setCustomerPrices([]));
  }, [filterCustomer]);

  const fetchProducts = async () => {
    const params = new URLSearchParams();
    params.set('status', filterStatus);
    if (filterCategory) params.set('category', filterCategory);
    const res = await api.get('/api/products/?' + params.toString());
    setProducts(res.data);
  };

  const fetchCategories = async () => {
    const res = await api.get('/api/products/categories');
    setCategories(res.data);
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [filterCategory, filterStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, labor_hours: form.labor_hours || null };
      if (editing) {
        await api.put(`/api/products/${editing.id}`, data);
      } else {
        await api.post('/api/products/', data);
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyProductForm());
      fetchProducts();
      fetchCategories();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleEdit = (product) => {
    setEditing(product);
    setForm({ ...emptyProductForm(), ...product, brand: product.brand || '', default_labor_price: product.default_labor_price || 0 });
    setShowForm(true);
  };

  const handlePriceHistory = async (product) => {
    const res = await api.get(`/api/products/${product.id}/price-history`);
    setPriceHistory(res.data);
    setPriceHistoryProduct(product);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ürünü silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/products/${id}`);
    fetchProducts();
  };

  const handleReactivate = async (id) => {
    await api.put(`/api/products/${id}`, { is_active: true });
    fetchProducts();
  };

  const handleDownload = async (url, filename) => {
    const res = await api.get(url, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([res.data]));
    a.download = filename;
    a.click();
  };

  const handleImport = async (e, endpoint) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      fetchProducts();
      fetchCategories();
    } catch (err) {
      alert('Import hatası: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setImporting(false);
      salesFileRef.current.value = '';
      purchaseFileRef.current.value = '';
    }
  };

  const openStockForm = async (product) => {
    if (locations.length === 0) {
      const res = await api.get('/api/locations/');
      setLocations(res.data);
    }
    setStockForm(emptyStockMovement());
    setStockProduct(product);
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/inventory/movements', {
        ...stockForm,
        product_id: stockProduct.id,
        location_id: parseInt(stockForm.location_id),
        quantity: parseFloat(stockForm.quantity),
      });
      setStockProduct(null);
      alert('Stok hareketi kaydedildi');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handlePurchaseHistory = async (product) => {
    const res = await api.get(`/api/purchase-prices/product/${product.id}`);
    setPurchaseHistory(res.data);
    setPurchaseHistoryProduct(product);
  };

  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr'));
  const customerPriceMap = new Map(customerPrices.map(cp => [cp.product_id, cp]));

  const q = search.trim().toLowerCase();
  const filteredProducts = products
    .filter(p => !q ||
      p.code.toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      (p.brand || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q))
    .filter(p => !filterBrand || p.brand === filterBrand)
    .filter(p => !filterCustomer || !onlyCustomerPriced || customerPriceMap.has(p.id))
    .map(p => {
      const cp = customerPriceMap.get(p.id);
      return {
        ...p,
        customer_sale_price: cp ? cp.sale_price : null,
      };
    });
  const sortedProducts = sortRows(filteredProducts, sort);
  const toggleProductSort = (key) => toggleSort(setSort, key);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Ürün / Hizmetler</h2>
        <button onClick={() => { setForm(emptyProductForm()); setEditing(null); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">
          + Yeni Ekle
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2">📤 Satış Fiyatları</h3>
          <p className="text-xs text-blue-600 mb-3">Ürün ekle/güncelle, müşteri özel fiyatları dahil</p>
          <div className="flex gap-2">
            <button onClick={() => handleDownload('/api/products/import/template/sales', 'satis_fiyatlari_sablonu.xlsx')} className="flex-1 bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-xs font-medium">📥 Şablon İndir</button>
            <button onClick={() => salesFileRef.current.click()} disabled={importing} className="flex-1 bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 text-xs font-medium disabled:opacity-50">
              {importing ? 'Yükleniyor...' : '⬆️ Excel Yükle'}
            </button>
            <input ref={salesFileRef} type="file" accept=".xlsx,.xls" onChange={e => handleImport(e, '/api/products/import/excel/sales')} className="hidden" />
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-semibold text-red-800 mb-2">🔒 Alış Fiyatları</h3>
          <p className="text-xs text-red-600 mb-3">Tedarikçi, tarih ve alış fiyatı güncelle</p>
          <div className="flex gap-2">
            <button onClick={() => handleDownload('/api/products/import/template/purchase', 'alis_fiyatlari_sablonu.xlsx')} className="flex-1 bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 text-xs font-medium">📥 Şablon İndir</button>
            <button onClick={() => purchaseFileRef.current.click()} disabled={importing} className="flex-1 bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600 text-xs font-medium disabled:opacity-50">
              {importing ? 'Yükleniyor...' : '⬆️ Excel Yükle'}
            </button>
            <input ref={purchaseFileRef} type="file" accept=".xlsx,.xls" onChange={e => handleImport(e, '/api/products/import/excel/purchase')} className="hidden" />
          </div>
        </div>
      </div>

      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-700 font-medium">{importResult.message}</p>
          {importResult.errors && importResult.errors.length > 0 && (
            <ul className="mt-2 text-red-600 text-sm">
              {importResult.errors.map((e, i) => <li key={i}>⚠️ {e}</li>)}
            </ul>
          )}
          <button onClick={() => setImportResult(null)} className="mt-2 text-sm text-gray-500 hover:text-gray-700">Kapat</button>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Kod, ad, marka, kategori veya açıklamada ara..."
          className="border rounded px-3 py-2 text-sm w-72"
        />
        <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}
          className={`border rounded px-3 py-2 text-sm ${filterCustomer ? 'border-purple-400 bg-purple-50 text-purple-800' : ''}`}>
          <option value="">Tüm Müşteriler (varsayılan fiyat)</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        {filterCustomer && (
          <label className="flex items-center gap-1 text-sm text-purple-700">
            <input type="checkbox" checked={onlyCustomerPriced} onChange={e => setOnlyCustomerPriced(e.target.checked)} />
            Sadece özel fiyatlılar ({customerPrices.length})
          </label>
        )}
        <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">Tüm Markalar</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">Tüm Kategoriler</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="active">Aktif</option>
          <option value="inactive">Pasif (Silinmiş)</option>
          <option value="all">Tümü</option>
        </select>
        <span className="text-sm text-gray-500">{sortedProducts.length} ürün</span>
        <button onClick={() => setShowPurchasePrices(!showPurchasePrices)} className="ml-auto text-sm text-red-600 hover:text-red-800 font-medium">
          {showPurchasePrices ? '🔒 Alış Fiyatlarını Gizle' : '🔓 Alış Fiyatlarını Göster'}
        </button>
      </div>

      {priceHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-1">Satış Fiyatı Geçmişi</h3>
            <p className="text-sm text-gray-500 mb-4">{priceHistoryProduct?.code} — {priceHistoryProduct?.name}</p>
            {priceHistory.length === 0 ? (
              <p className="text-gray-400 text-center py-6">Henüz fiyat değişikliği kaydı yok</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-500">Tarih</th>
                    <th className="text-left px-3 py-2 text-gray-500">Müşteri</th>
                    <th className="text-right px-3 py-2 text-gray-500">Satış</th>
                    <th className="text-right px-3 py-2 text-gray-500">İşçilik ₺</th>
                    <th className="text-left px-3 py-2 text-gray-500">Kaynak</th>
                    <th className="text-left px-3 py-2 text-gray-500">Değiştiren</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {priceHistory.map(h => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{h.created_at ? new Date(h.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                      <td className="px-3 py-2">{h.customer_name ? <span className="text-purple-700">{h.customer_name}</span> : <span className="text-gray-500">Varsayılan</span>}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmt(h.sale_price)} {h.currency}</td>
                      <td className="px-3 py-2 text-right text-green-700">{h.labor_price ? fmt(h.labor_price) : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{SOURCE_LABELS[h.source] || h.source || '-'}</td>
                      <td className="px-3 py-2 text-gray-500">{h.changed_by || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button onClick={() => setPriceHistory(null)} className="mt-4 w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">Kapat</button>
          </div>
        </div>
      )}

      {purchaseHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold mb-1">Alış Fiyatı Geçmişi</h3>
            <p className="text-sm text-gray-500 mb-4">{purchaseHistoryProduct?.name}</p>
            {purchaseHistory.length === 0 ? (
              <p className="text-gray-400 text-center py-6">Henüz alış fiyatı girilmemiş</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-500">Tarih</th>
                    <th className="text-left px-3 py-2 text-gray-500">Tedarikçi</th>
                    <th className="text-right px-3 py-2 text-gray-500">Alış Fiyatı</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchaseHistory.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600">{p.price_date || '-'}</td>
                      <td className="px-3 py-2 text-gray-800">{p.supplier || '-'}</td>
                      <td className="px-3 py-2 text-right font-medium text-red-600">{p.purchase_price.toFixed(2)} {p.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button onClick={() => setPurchaseHistory(null)} className="mt-4 w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">Kapat</button>
          </div>
        </div>
      )}

      {stockProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-1">Stok Hareketi Ekle</h3>
            <p className="text-sm text-gray-500 mb-4">{stockProduct.code} - {stockProduct.name}</p>
            <form onSubmit={handleStockSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Depo *</label>
                  <select required value={stockForm.location_id} onChange={e => setStockForm({...stockForm, location_id: e.target.value})} className="w-full border rounded px-3 py-2">
                    <option value="">Seçiniz</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hareket Türü *</label>
                  <select required value={stockForm.movement_type} onChange={e => setStockForm({...stockForm, movement_type: e.target.value})} className="w-full border rounded px-3 py-2">
                    <option value="in">Giriş</option>
                    <option value="out">Çıkış</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Miktar *</label>
                  <input required type="number" step="0.01" min="0.01" value={stockForm.quantity} onChange={e => setStockForm({...stockForm, quantity: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tarih *</label>
                  <input required type="date" value={stockForm.movement_date} onChange={e => setStockForm({...stockForm, movement_date: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Neden</label>
                  <input value={stockForm.reason} onChange={e => setStockForm({...stockForm, reason: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="Satın alma, sevkiyat..." />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                  <textarea value={stockForm.note} onChange={e => setStockForm({...stockForm, note: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Kaydet</button>
                <button type="button" onClick={() => setStockProduct(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">{editing ? 'Ürün Düzenle' : 'Yeni Ürün/Hizmet'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kod *</label>
                  <input required value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategori *</label>
                  <input required value={form.category} onChange={e => setForm({...form, category: e.target.value})} list="categories-list" className="w-full border rounded px-3 py-2" placeholder="Yazın veya seçin" />
                  <datalist id="categories-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad *</label>
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marka</label>
                  <input value={form.brand || ''} onChange={e => setForm({...form, brand: e.target.value})} list="brands-list" className="w-full border rounded px-3 py-2" />
                  <datalist id="brands-list">
                    {brands.map(b => <option key={b} value={b} />)}
                  </datalist>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                  <textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Birim</label>
                  <select value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} className="w-full border rounded px-3 py-2">
                    {['Adet', 'Metre', 'Saat', 'Gün', 'Paket', 'Set', 'KVA', 'KW', 'Kutu'].map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Para Birimi</label>
                  <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} className="w-full border rounded px-3 py-2">
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alış Fiyatı</label>
                  <input type="number" step="0.01" value={form.default_purchase_price} onChange={e => setForm({...form, default_purchase_price: parseFloat(e.target.value)})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Satış Fiyatı</label>
                  <input type="number" step="0.01" value={form.default_sale_price} onChange={e => setForm({...form, default_sale_price: parseFloat(e.target.value)})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İşçilik Birim Fiyatı (₺)</label>
                  <input type="number" step="0.01" value={form.default_labor_price} onChange={e => setForm({...form, default_labor_price: parseFloat(e.target.value) || 0})} className="w-full border rounded px-3 py-2" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Kaydet</button>
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableTh label="Kod" sortKey="code" sort={sort} onSort={toggleProductSort} />
              <SortableTh label="Kategori" sortKey="category" sort={sort} onSort={toggleProductSort} />
              <SortableTh label="Ad" sortKey="name" sort={sort} onSort={toggleProductSort} />
              <SortableTh label="Marka" sortKey="brand" sort={sort} onSort={toggleProductSort} />
              <SortableTh label="Birim" sortKey="unit" sort={sort} onSort={toggleProductSort} />
              {showPurchasePrices && <SortableTh label="Alış" sortKey="default_purchase_price" sort={sort} onSort={toggleProductSort} align="right" />}
              <SortableTh label="Satış" sortKey="default_sale_price" sort={sort} onSort={toggleProductSort} align="right" />
              <SortableTh label="İşçilik ₺" sortKey="default_labor_price" sort={sort} onSort={toggleProductSort} align="right" />
              {filterCustomer && <SortableTh label="Müşteri Fiyatı" sortKey="customer_sale_price" sort={sort} onSort={toggleProductSort} align="right" />}
              <SortableTh label="Durum" sortKey="is_active" sort={sort} onSort={toggleProductSort} />
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedProducts.map(p => (
              <tr key={p.id} className={`hover:bg-gray-50 ${!p.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 text-sm font-mono text-gray-600">{p.code}</td>
                <td className="px-4 py-3"><span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded">{p.category}</span></td>
                <td className="px-4 py-3 font-medium text-gray-800">{p.name}</td>
                <td className="px-4 py-3 text-gray-600 text-sm">{p.brand || '-'}</td>
                <td className="px-4 py-3 text-gray-600">{p.unit}</td>
                {showPurchasePrices && <td className="px-4 py-3 text-right text-red-500 text-sm">{p.default_purchase_price.toFixed(2)} {p.currency}</td>}
                <td className="px-4 py-3 text-right font-medium text-gray-800 whitespace-nowrap">
                  {p.default_sale_price
                    ? `${fmt(p.default_sale_price)} ${p.currency}`
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-right text-green-700 text-sm whitespace-nowrap">{p.default_labor_price ? fmt(p.default_labor_price) : <span className="text-gray-300">—</span>}</td>
                {filterCustomer && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {customerPriceMap.has(p.id) ? (
                      <div>
                        <div className="font-medium text-purple-700">★ {fmt(customerPriceMap.get(p.id).sale_price)} {customerPriceMap.get(p.id).currency}</div>
                        {customerPriceMap.get(p.id).labor_price ? <div className="text-xs text-green-700">İşç. ₺{fmt(customerPriceMap.get(p.id).labor_price)}</div> : null}
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                )}
                <td className="px-4 py-3">
                  {p.is_active
                    ? <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded font-medium">Aktif</span>
                    : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded font-medium">Pasif</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => handlePriceHistory(p)} className="text-purple-600 hover:text-purple-800 font-medium text-sm mr-2">Fiyat Geçmişi</button>
                  <button onClick={() => handlePurchaseHistory(p)} className="text-orange-500 hover:text-orange-700 font-medium text-sm mr-2">Alış</button>
                  <button onClick={() => openStockForm(p)} className="text-teal-600 hover:text-teal-800 font-medium text-sm mr-2">Stok</button>
                  <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-2">Düzenle</button>
                  {p.is_active
                    ? <button onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700 font-medium text-sm">Sil</button>
                    : <button onClick={() => handleReactivate(p.id)} className="text-green-600 hover:text-green-800 font-medium text-sm">Aktif Yap</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedProducts.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {products.length === 0 ? 'Henüz ürün/hizmet eklenmemiş' : 'Filtrelerle eşleşen ürün bulunamadı'}
          </div>
        )}
      </div>
    </div>
  );
}
